import React from 'react';
import Title from 'components/common/typeography/title/Title';
import {formatCurrency} from 'lib/currency';
import {Trans, useTranslation} from 'lib/i18n';
import {Box, useTheme} from '@material-ui/core';
import SmallPieChart from 'pages/illustrator/components/product-printout-page/components/payment-breakdown/components/small-pie-chart/SmallPieChart';
import SmallPieChartWithHorizontalLegend from 'pages/illustrator/components/product-printout-page/components/payment-breakdown/components/small-pie-chart-with-horizontal-legend/SmallPieChartWithHorizontalLegend';
import useStyles from './ProductWidgetStyles';
import Text from 'components/common/typeography/text/Text';
import {API_ROUTES} from 'constants/illustrator';

const AVAILABLE_CASH_API_ROUTES = new Set([
  API_ROUTES.TIER,
  API_ROUTES.FIXED,
  API_ROUTES.INTRO,
  API_ROUTES.SECOND,
]);

function ProductWidgetData(product, t, theme) {
  if (!product?.calculation) {
    return {};
  }

  const liens = product.calculation.BorrowerProfile.Liens;
  const remainingEquity = product.calculation.RemainingEquity;
  const usesAvailableCash = AVAILABLE_CASH_API_ROUTES.has(product.ApiRoute);
  const availableProceeds = usesAvailableCash
    ? product.calculation.SimplifiedCashToBorrower
    : product.calculation.LineOfCredit;

  return [
    {
      label: t('product widget.mortgage balance'),
      value: Math.abs(liens),
      tooltip: () => liens,
      color: theme.palette.widget.dark,
    },
    {
      label: t(usesAvailableCash ? 'product widget.available cash' : 'product widget.available loc'),
      value: Math.abs(availableProceeds),
      tooltip: () => availableProceeds,
      color: theme.palette.widget.main,
    },
    {
      label: t('product widget.remaining equity'),
      value: Math.abs(remainingEquity),
      tooltip: () => remainingEquity,
      color: theme.palette.widget.light,
    },
  ];
}

function ProductWidget(props) {
  const theme = useTheme();
  const {t} = useTranslation();

  const { product } = props;

  const displayName = product?.WidgetDisplayName ?? product?.ProductName;
  const PrincipalLimit = product?.calculation?.PrincipalLimit;
  const CalculationId = product?.calculation?.CalculationId;
  
  const classes = useStyles();

  const data4pieChart = ProductWidgetData(product, t, theme);

  if (!CalculationId) return null;

  return (
    <div className={classes.productWidgetContainer}>
      <Box className={classes.productTitleSection}>
        <Title variant="h5">{displayName}</Title>
      </Box>
      <Box className={classes.productProceedsSection}>
        <Title variant="h6">
          <Trans i18nKey="product widget.total proceeds" />
        </Title>
        <Title variant="h4">{formatCurrency(PrincipalLimit)}</Title>
      </Box>

      <Box className={classes.smPieChartWidgetContainer}>
        <SmallPieChart key={CalculationId} data={data4pieChart} innerRadius={20} />
      </Box>

      {data4pieChart.map((data, index) => (
        <Box key={index} className={classes.productLegendRow}>
          <SmallPieChartWithHorizontalLegend
            className={classes.productLegendItem}
            titleClassName={classes.productLegendTitleContainer}
            title={
              <Title className={classes.productPieChartTitle} variant="h6">
                {data.label}
              </Title>
            }
            color={data.color}
            value={
              <Text className={classes.productPieChartText} variant="body1">
                {formatCurrency(data.tooltip())}
              </Text>
            }
          />
        </Box>
      ))}
    </div>
  );
}

ProductWidget.propTypes = {};

export default ProductWidget;
